// CallsTable -- shared call-history table used by the Calls page and the
// Calls tab on customers / leads. Rows come from GET /api/calls.
//
// Recordings are fetched through Command (GET /api/calls/:id/recording,
// which proxies 3CX) with the user's token, then played from a blob URL,
// so the 3CX key never reaches the browser.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PhoneLink from './PhoneLink.jsx';
import { Message } from './EmailsTab.jsx';

export function formatPhone(n) {
  const d = String(n || '').replace(/\D/g, '');
  if (d.length < 10) return n || '';
  const t = d.slice(-10);
  return `(${t.slice(0, 3)}) ${t.slice(3, 6)}-${t.slice(6)}`;
}

export function formatSeconds(s) {
  if (s == null) return '';
  const n = Math.max(0, Math.round(s));
  if (n < 60) return `${n}s`;
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const r = n % 60;
  if (h) return `${h}h ${m}m`;
  return r ? `${m}m ${r}s` : `${m}m`;
}

function formatWhen(iso) {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  const time = d.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today ${time}`;
  if (d.toDateString() === yest.toDateString()) return `Yesterday ${time}`;
  return `${d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' })} ${time}`;
}

function recordPath(row) {
  if (!row.contact_id) return null;
  switch (row.record_type) {
    case 'customer': return `/customers/${row.contact_id}`;
    case 'lead': return `/leads/${row.contact_id}`;
    case 'distributor': return `/distributors/${row.contact_id}`;
    case 'strategic_account': return `/strategic-accounts/${row.contact_id}`;
    case 'vendor_contact': return `/vendor-contacts/${row.contact_id}`;
    default: return null;
  }
}

function callerName(row) {
  const person = [row.first_name, row.last_name].filter(Boolean).join(' ');
  return row.company || person || null;
}

function personName(row) {
  if (row.answered_by_name) return row.answered_by_name.split(' ')[0];
  if (row.answered_name) return row.answered_name.split(' ')[0];
  return row.answered_ext ? `ext ${row.answered_ext}` : '?';
}

function Result({ row }) {
  if (row.direction === 'internal') {
    return row.answered
      ? <span className="calls-result calls-result-good">Answered by {personName(row)}</span>
      : <span className="calls-result calls-result-muted">No answer</span>;
  }
  if (row.direction === 'outbound') {
    return row.answered
      ? <span className="calls-result">{personName(row)} called</span>
      : <span className="calls-result calls-result-muted">No answer</span>;
  }
  if (row.answered) {
    return <span className="calls-result calls-result-good">Answered by {personName(row)}</span>;
  }
  return (
    <span className="calls-result calls-result-bad">
      Missed{row.voice_agent ? <span className="calls-sub-inline"> · Voice Agent</span> : null}
      {row.unreturned ? <span className="badge badge-amber calls-unreturned">Not returned</span> : null}
    </span>
  );
}

function RecordingPlayer({ callId, part = 0 }) {
  const [state, setState] = useState({ loading: true, url: null, error: null });
  useEffect(() => {
    let url = null;
    let cancelled = false;
    axios.get(`/api/calls/${callId}/recording`, { params: { part }, responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setState({ loading: false, url, error: null });
      })
      .catch(async (err) => {
        let msg = 'Could not load recording';
        try {
          const text = await err.response.data.text();
          msg = JSON.parse(text).error || msg;
        } catch { /* keep default */ }
        if (!cancelled) setState({ loading: false, url: null, error: msg });
      });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [callId, part]);

  if (state.loading) return <span className="calls-dim">Loading recording…</span>;
  if (state.error) return <span className="calls-error">{state.error}</span>;
  return (
    <span className="calls-player">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio controls autoPlay={part === 0} src={state.url} />
      <a href={state.url} download={`call-${callId}${part ? `-part${part + 1}` : ''}.wav`} className="calls-link">Download</a>
    </span>
  );
}

// 3CX AI transcript + summary for a recorded call. If 3CX hasn't
// transcribed it yet, offers "Transcribe now" and polls until ready.
function TranscriptPanel({ callId, part = 0 }) {
  const [state, setState] = useState({ loading: true });

  const load = async () => {
    try {
      const r = await axios.get(`/api/calls/${callId}/transcript`, { params: { part } });
      setState({ loading: false, ...r.data });
      return r.data;
    } catch (err) {
      setState({ loading: false, error: err.response?.data?.error || err.message });
      return null;
    }
  };

  useEffect(() => { load(); }, [callId, part]); // eslint-disable-line react-hooks/exhaustive-deps

  async function transcribe() {
    setState((s) => ({ ...s, working: true, error: null }));
    try {
      await axios.post(`/api/calls/${callId}/transcript`, null, { params: { part } });
    } catch (err) {
      setState((s) => ({ ...s, working: false, error: err.response?.data?.error || err.message }));
      return;
    }
    for (let i = 0; i < 36; i += 1) {
      await new Promise((r) => setTimeout(r, 5000));
      const d = await load();
      if (d && d.status === 'ready' && (d.text || d.summary)) return;
    }
    setState((s) => ({ ...s, working: false, error: '3CX is still working on it. Try again in a few minutes.' }));
  }

  if (state.loading) return <span className="calls-dim">Loading transcript…</span>;
  if (state.error && state.status !== 'not_transcribed') return <span className="calls-error">{state.error}</span>;
  if (state.status === 'not_transcribed') {
    return (
      <div className="calls-transcript">
        <span className="calls-dim">Not transcribed yet. </span>
        {state.can_transcribe
          ? <button type="button" className="calls-play" onClick={transcribe} disabled={state.working}>{state.working ? 'Transcribing…' : 'Transcribe now'}</button>
          : <span className="calls-dim">3CX can't transcribe this recording.</span>}
        {state.error && <div className="calls-error">{state.error}</div>}
      </div>
    );
  }

  const text = state.text || '';
  return (
    <div className="calls-transcript">
      {state.summary && (
        <div className="calls-transcript-summary">
          <div className="calls-label">AI summary{state.sentiment != null ? ` · sentiment ${state.sentiment}/5` : ''}</div>
          <div>{state.summary}</div>
        </div>
      )}
      <div className="calls-label">Transcript</div>
      <div className="calls-transcript-text">{text || <span className="calls-dim">3CX returned no transcript text for this call.</span>}</div>
      {!text && !state.summary && (
        <div className="calls-filters" style={{ marginTop: 'var(--space-2)' }}>
          <button type="button" className="calls-play" onClick={transcribe} disabled={state.working}>
            {state.working ? 'Transcribing…' : 'Transcribe again'}
          </button>
          {state.error && <span className="calls-error">{state.error}</span>}
        </div>
      )}
      {state.debug && (
        <details>
          <summary className="calls-dim">What 3CX sent (for troubleshooting)</summary>
          <pre className="calls-sample">{JSON.stringify(state.debug, null, 2)}</pre>
        </details>
      )}
      {text && (
        <button type="button" className="calls-link calls-copy" onClick={() => navigator.clipboard && navigator.clipboard.writeText(text)}>Copy transcript</button>
      )}
    </div>
  );
}

// A call can have several recorded parts (e.g. Dave answered, then
// transferred to Matthew): one labelled player / transcript per part.
function partLabel(p) {
  if (!p) return '';
  if (p.name && p.ext) return `${p.name} (ext ${p.ext})`;
  return p.name || (p.ext ? `Ext ${p.ext}` : `Part ${p.part + 1}`);
}

function RecordingParts({ row }) {
  const parts = row.recording_parts && row.recording_parts.length ? row.recording_parts : [{ part: 0 }];
  if (parts.length === 1) return <RecordingPlayer callId={row.id} part={0} />;
  return (
    <div className="calls-parts">
      {parts.map((p) => (
        <div key={p.part} className="calls-part">
          <div className="calls-label">{partLabel(p)}</div>
          <RecordingPlayer callId={row.id} part={p.part} />
        </div>
      ))}
    </div>
  );
}

function TranscriptParts({ row }) {
  const parts = row.recording_parts && row.recording_parts.length ? row.recording_parts : [{ part: 0 }];
  if (parts.length === 1) return <TranscriptPanel callId={row.id} part={0} />;
  return (
    <div className="calls-parts">
      {parts.map((p) => (
        <div key={p.part} className="calls-part">
          <div className="calls-part-title">{partLabel(p)}</div>
          <TranscriptPanel callId={row.id} part={p.part} />
        </div>
      ))}
    </div>
  );
}

export default function CallsTable({ rows, emails = [], emailUsers = [], onEmailChanged, showCaller = true, emptyText = 'No calls.' }) {
  const [playing, setPlaying] = useState(null);
  const [transcriptFor, setTranscriptFor] = useState(null);
  const [openEmail, setOpenEmail] = useState(null);
  const cols = showCaller ? 8 : 7;

  // Calls and support emails in one timeline, newest first.
  const merged = [
    ...rows.map((r) => ({ kind: 'call', t: new Date(r.started_at).getTime(), row: r })),
    ...emails.map((m) => ({ kind: 'email', t: new Date(m.activity_at || m.sent_at).getTime(), row: m }))
  ].sort((a, b) => b.t - a.t);

  // Our own people by first name (david@xsitegroup.ca -> David), anyone
  // else by full address.
  const who = (a) => {
    const [local, dom] = String(a || '').toLowerCase().split('@');
    if (dom === 'xsitegroup.ca' && local) return local.charAt(0).toUpperCase() + local.slice(1);
    return a;
  };
  const people = (list) => (list || []).map(who).join(', ');

  async function updateThread(m, patch) {
    try {
      await axios.patch('/api/customer-emails/thread', { thread_key: m.thread_key, contact_id: m.contact_id, ...patch });
      if (onEmailChanged) onEmailChanged();
    } catch (e) {
      window.alert(e.response?.data?.error || e.message); // eslint-disable-line no-alert
    }
  }

  function ThreadStatus({ m }) {
    const bits = [];
    if (m.status === 'done') {
      bits.push(<span key="d" className="th-chip th-done">✓ Done{m.done_by_name ? ` · ${m.done_by_name.split(' ')[0]}` : ''}{m.done_at ? ` · ${formatWhen(m.done_at)}` : ''}</span>);
    } else if (m.waiting_on === 'customer' && m.last_reply) {
      bits.push(<span key="r" className="th-chip th-replied">Replied by {who(m.last_reply.from_address)} · {formatWhen(m.last_reply.sent_at)}</span>);
    } else {
      bits.push(<span key="w" className="th-chip th-waiting">Waiting on us</span>);
    }
    const fwdTo = [...new Set((m.forwards || []).flatMap((f) => (f.to || []).map(who)))];
    if (fwdTo.length) bits.push(<span key="f" className="th-chip th-fwd">Forwarded to {fwdTo.join(', ')}</span>);
    if (m.internal_notes) bits.push(<span key="n" className="th-chip th-note">{m.internal_notes} internal repl{m.internal_notes === 1 ? 'y' : 'ies'}</span>);
    return <div className="th-status">{bits}</div>;
  }

  function renderEmail(m) {
    const isOpen = openEmail === m.id;
    return [
      <tr key={`e-${m.id}`} className="calls-row-email">
        <td className="calls-when">{formatWhen(m.sent_at)}</td>
        {showCaller && (
          <td>
            <Link to={`/customers/${m.contact_id}?tab=emails${m.site_id ? `&email_site=${m.site_id}` : ''}`}>{m.company || `Customer #${m.contact_id}`}</Link>
            <div className="calls-sub">{m.from_name || m.from_address}</div>
            {(m.site_name || m.site_number) && <div className="calls-sub">{m.site_name}{m.site_number ? ` #${m.site_number}` : ''}</div>}
          </td>
        )}
        <td><span className="calls-email-addr">{m.from_address}</span>
          {!showCaller && (m.site_name || m.site_number) && <div className="calls-sub">{m.site_name}{m.site_number ? ` #${m.site_number}` : ''}</div>}
        </td>
        <td><span className="calls-dir calls-dir-email">✉ Email</span></td>
        <td colSpan={3} className="calls-email-subj">
          <span className="em-cat em-cat-support">Support</span> {m.subject || '(no subject)'}
          {m.count > 1 && <span className="em-count"> {m.count}</span>}
          {m.has_attachments && ' 📎'}
          <ThreadStatus m={m} />
          <div className="calls-sub calls-email-to">
            {m.to_addresses?.length > 0 && <span><strong>To</strong> {people(m.to_addresses)}</span>}
            {m.cc_addresses?.length > 0 && <span><strong>CC</strong> {people(m.cc_addresses)}</span>}
          </div>
        </td>
        <td className="th-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpenEmail(isOpen ? null : m.id)}>
            {isOpen ? 'Close' : 'Open'}
          </button>
          <select
            className="th-assign"
            value={m.assigned_user_id || ''}
            onChange={(e) => updateThread(m, { assigned_user_id: e.target.value || null })}
            title="Assign this thread"
          >
            <option value="">Unassigned</option>
            {emailUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {m.status === 'done'
            ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => updateThread(m, { status: 'open' })}>Reopen</button>
            : <button type="button" className="btn btn-primary btn-sm" onClick={() => updateThread(m, { status: 'done' })}>Done</button>}
        </td>
      </tr>,
      isOpen && (
        <tr key={`e-${m.id}-body`} className="calls-player-row">
          <td colSpan={cols}><Message m={m} /></td>
        </tr>
      )
    ];
  }

  return (
    <div className="calls-table-wrap">
      <table className="data-table calls-table">
        <thead>
          <tr>
            <th>When</th>
            {showCaller && <th>Caller</th>}
            <th>Number</th>
            <th>Direction</th>
            <th>Result</th>
            <th className="calls-num">Ring</th>
            <th className="calls-num">Talk</th>
            <th>Recording</th>
          </tr>
        </thead>
        <tbody>
          {merged.map((item) => {
            if (item.kind === 'email') return renderEmail(item.row);
            const r = item.row;
            const name = callerName(r);
            const path = recordPath(r);
            return [
              <tr key={r.id} className={!r.answered && r.direction === 'inbound' ? 'calls-row-missed' : ''}>
                <td className="calls-when">{formatWhen(r.started_at)}</td>
                {showCaller && (
                  <td>
                    {r.direction === 'internal'
                      ? <span>{r.caller_name || `Ext ${r.from_ext}`} <span className="calls-dim">(internal)</span></span>
                      : (name && path ? <Link to={path}>{name}</Link> : (name || r.caller_name || <span className="calls-dim">Unknown</span>))}
                    {r.direction !== 'internal' && r.caller_person && r.caller_person !== name && (
                      <div className="calls-sub">{r.caller_person}</div>
                    )}
                    {r.site_name && <div className="calls-sub">{r.site_name}{r.site_number ? ` #${r.site_number}` : ''}</div>}
                  </td>
                )}
                <td>
                  <span className="calls-mono">
                    {r.direction === 'internal'
                      ? `Ext ${r.from_ext || '?'} → ${r.answered_ext || (r.rang_exts && r.rang_exts[0]) || '?'}`
                      : <PhoneLink number={formatPhone(r.external_number)} />}
                  </span>
                  {!showCaller && r.direction !== 'internal' && r.caller_person && <div className="calls-sub">{r.caller_person}</div>}
                  {!showCaller && r.site_name && <div className="calls-sub">{r.site_name}{r.site_number ? ` #${r.site_number}` : ''}</div>}
                </td>
                <td>
                  <span className={`calls-dir calls-dir-${r.direction}`}>
                    {r.direction === 'inbound' ? '↙ In' : r.direction === 'outbound' ? '↗ Out' : '⇄ Internal'}
                  </span>
                </td>
                <td><Result row={r} /></td>
                <td className="calls-num calls-mono">{r.answered || r.direction === 'inbound' ? formatSeconds(r.ring_seconds) : ''}</td>
                <td className="calls-num calls-mono">{r.answered ? formatSeconds(r.talk_seconds) : ''}</td>
                <td>
                  {r.has_recording ? (
                    <span className="calls-rec-actions">
                      <button type="button" className="calls-play" onClick={() => setPlaying(playing === r.id ? null : r.id)}>
                        {playing === r.id ? 'Close' : `▶ Play${r.recording_parts && r.recording_parts.length > 1 ? ` (${r.recording_parts.length})` : ''}`}
                      </button>
                      <button type="button" className="calls-play" onClick={() => setTranscriptFor(transcriptFor === r.id ? null : r.id)}>
                        {transcriptFor === r.id ? 'Hide text' : 'Transcript'}
                      </button>
                    </span>
                  ) : null}
                </td>
              </tr>,
              playing === r.id && (
                <tr key={`${r.id}-player`} className="calls-player-row">
                  <td colSpan={cols}><RecordingParts row={r} /></td>
                </tr>
              ),
              transcriptFor === r.id && (
                <tr key={`${r.id}-transcript`} className="calls-player-row">
                  <td colSpan={cols}><TranscriptParts row={r} /></td>
                </tr>
              )
            ];
          })}
          {merged.length === 0 && (
            <tr><td colSpan={cols} className="calls-empty">{emptyText}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
